import { Menu, MenuItem } from "@mui/material";
import { useEffect, useMemo } from "react";
import { setLanguage } from "actions/home/home";
import { updateSettings } from "actions/authentication/authentication";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import { useSearchParams } from "react-router-dom";
import { authentication } from "actions";
import { useTranslations } from "hooks/UseTranslations";
import useSnackbar from "hooks/UseSnackBar/useSnackBar";

type LanguageCode = "en" | "de" | "fr" | "es";

interface Props {
    handleLanguageOpen: (event: React.MouseEvent<HTMLElement> | null) => void;
    languageAnchorEl: null | HTMLElement;
    classes: Record<string, string>;
}
export default function LanguageDropdown({ handleLanguageOpen, languageAnchorEl, classes }: Props) {
    const openMenu = Boolean(languageAnchorEl);
    const dispatch = useAppDispatch();
    const { translations } = useTranslations();
    const { language } = useAppSelector((state) => state.homePageReducer);
    const [searchParams, setSearchParams] = useSearchParams();
    const { showSnackBar } = useSnackbar();

    const languageOptions = useMemo(
        () => [
            { code: "en" as LanguageCode, label: translations("english"), shortLabel: "EN" },
            { code: "de" as LanguageCode, label: translations("german"), shortLabel: "DE" },
            { code: "fr" as LanguageCode, label: translations("french"), shortLabel: "FR" },
            { code: "es" as LanguageCode, label: translations("spanish"), shortLabel: "ES" },
        ],
        [translations]
    );

    const changeLanguage = async (lang: LanguageCode) => {
        localStorage.setItem("lang", lang);
        dispatch(setLanguage(lang));
        dayjs.locale(lang);
        handleLanguageOpen(null);

        if (searchParams.has("language")) {
            searchParams.delete("language");
            setSearchParams(searchParams);
        }

        const res = await dispatch(updateSettings({ language: lang }));
        if (res.type === authentication.UPDATE_USER_SETTINGS_SUCCESS) {
            dispatch(setLanguage(lang));
            showSnackBar(translations("error.language_changed_successfuly"), "green");
        }
    };

    useEffect(() => {
        console.log("localStorage.getItem('lang')", localStorage.getItem("lang"));
        const storedLanguage = (localStorage.getItem("lang") as LanguageCode | null) || "de";
        dispatch(setLanguage(storedLanguage));
    }, [dispatch]);

    return (
        <Menu
            anchorEl={languageAnchorEl}
            open={openMenu}
            onClose={() => handleLanguageOpen(null)}
            className={classes.popupMenu}>
            {languageOptions.map(({ code, label, shortLabel }) => (
                <MenuItem
                    key={code}
                    className={language === code ? `${classes.menuItem} ${classes.active}` : classes.menuItem}
                    onClick={() => changeLanguage(code)}>
                    {label} ({shortLabel})
                </MenuItem>
            ))}
        </Menu>
    );
}
